import { useEffect, useMemo, useState } from "react";
import {
  Card,
  DataTable,
  PageHeader,
  SelectInput,
  type DataTableColumn,
  type ProjectWorkItem,
} from "@pericles/framework-frontend";

import { fetchProjectsForCustomer, type ProjectSummary } from "../api/projectsClient";
import { fetchProjectWorkItems } from "../api/projectWorkItemsClient";

import { List, ListItem, PageShell } from "./ui";
interface PortfolioScreenProps {
  activeCustomerId: string;
}

type SortKey = "score" | "impact" | "effort" | "roi" | "title";

type SortDirection = "asc" | "desc";

function computeScore(item: ProjectWorkItem): number {
  const impact = item.impact ?? 0;
  const effort = item.effort ?? 0;
  const roi = item.roi ?? 0;
  const divisor = effort > 0 ? effort : 1;
  return (impact * roi) / divisor;
}

function formatScore(value: number): string {
  if (!Number.isFinite(value)) return "-";
  return value.toFixed(2);
}

function formatDependencies(value?: string[] | null): string {
  if (!value || value.length === 0) return "-";
  return value.join(", ");
}

function buildRunOrder(items: ProjectWorkItem[]): { order: ProjectWorkItem[]; hasCycle: boolean } {
  const ids = new Set(items.map((item) => String(item.id)));
  const dependencies = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();

  items.forEach((item) => {
    const id = String(item.id);
    inDegree.set(id, 0);
    const deps = (item.dependencies ?? []).map(String).filter((dep) => ids.has(dep) && dep !== id);
    dependencies.set(id, deps);
  });

  dependencies.forEach((deps, id) => {
    deps.forEach((dep) => {
      const current = outgoing.get(dep) ?? [];
      outgoing.set(dep, [...current, id]);
      inDegree.set(id, (inDegree.get(id) ?? 0) + 1);
    });
  });

  const scoreById = new Map(items.map((item) => [String(item.id), computeScore(item)]));
  const available: string[] = [];
  inDegree.forEach((count, id) => {
    if (count === 0) available.push(id);
  });

  const order: ProjectWorkItem[] = [];
  const itemById = new Map(items.map((item) => [String(item.id), item]));

  const sortAvailable = () =>
    available.sort((a, b) => {
      const scoreA = scoreById.get(a) ?? 0;
      const scoreB = scoreById.get(b) ?? 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      const titleA = itemById.get(a)?.title ?? "";
      const titleB = itemById.get(b)?.title ?? "";
      return titleA.localeCompare(titleB);
    });

  while (available.length > 0) {
    sortAvailable();
    const nextId = available.shift();
    if (!nextId) break;
    const item = itemById.get(nextId);
    if (item) {
      order.push(item);
    }
    const nextNodes = outgoing.get(nextId) ?? [];
    nextNodes.forEach((node) => {
      const nextCount = (inDegree.get(node) ?? 0) - 1;
      inDegree.set(node, nextCount);
      if (nextCount === 0) {
        available.push(node);
      }
    });
  }

  const remaining = items.filter((item) => !order.includes(item));
  const hasCycle = remaining.length > 0;
  if (remaining.length > 0) {
    remaining.sort((a, b) => computeScore(b) - computeScore(a));
    order.push(...remaining);
  }

  return { order, hasCycle };
}

export function PortfolioScreen({ activeCustomerId }: PortfolioScreenProps): JSX.Element {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [workItems, setWorkItems] = useState<ProjectWorkItem[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [workItemsLoading, setWorkItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  useEffect(() => {
    if (activeCustomerId === "all") {
      setProjects([]);
      setSelectedProjectId("");
      setWorkItems([]);
      return;
    }

    let isMounted = true;
    setProjectsLoading(true);
    setError(null);

    fetchProjectsForCustomer(activeCustomerId)
      .then((items) => {
        if (!isMounted) return;
        setProjects(items);
        if (items.length > 0) {
          setSelectedProjectId((current) => current || String(items[0].id));
        } else {
          setSelectedProjectId("");
          setWorkItems([]);
        }
      })
      .catch((fetchError) => {
        if (!isMounted) return;
        const message = fetchError instanceof Error ? fetchError.message : "Failed to load projects.";
        setError(message);
      })
      .finally(() => {
        if (isMounted) setProjectsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeCustomerId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setWorkItems([]);
      return;
    }

    let isMounted = true;
    setWorkItemsLoading(true);
    setError(null);

    fetchProjectWorkItems(selectedProjectId)
      .then((items) => {
        if (!isMounted) return;
        setWorkItems(items);
      })
      .catch((fetchError) => {
        if (!isMounted) return;
        const message = fetchError instanceof Error ? fetchError.message : "Failed to load work items.";
        setError(message);
      })
      .finally(() => {
        if (isMounted) setWorkItemsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedProjectId]);

  const scoredItems = useMemo(() =>
    workItems.map((item) => ({
      ...item,
      score: computeScore(item),
    })), [workItems]);

  const sortedItems = useMemo(() => {
    const direction = sortDirection === "asc" ? 1 : -1;
    return [...scoredItems].sort((a, b) => {
      if (sortKey === "title") {
        return a.title.localeCompare(b.title) * direction;
      }
      const aValue = sortKey === "score" ? a.score : a[sortKey] ?? 0;
      const bValue = sortKey === "score" ? b.score : b[sortKey] ?? 0;
      if (aValue === bValue) {
        return a.title.localeCompare(b.title) * direction;
      }
      return (aValue > bValue ? 1 : -1) * direction;
    });
  }, [scoredItems, sortDirection, sortKey]);

  const runOrder = useMemo(() => buildRunOrder(scoredItems), [scoredItems]);

  const columns = useMemo<DataTableColumn<ProjectWorkItem & { score: number }>[]>(
    () => [
      {
        id: "title",
        header: "Title",
        accessor: (row) => <div className="font-semibold text-slate-900">{row.title}</div>,
      },
      {
        id: "impact",
        header: "Impact",
        accessor: (row) => row.impact ?? "-",
      },
      {
        id: "effort",
        header: "Effort",
        accessor: (row) => row.effort ?? "-",
      },
      {
        id: "roi",
        header: "ROI",
        accessor: (row) => row.roi ?? "-",
      },
      {
        id: "score",
        header: "Score",
        accessor: (row) => formatScore(row.score),
      },
      {
        id: "dependencies",
        header: "Dependencies",
        accessor: (row) => formatDependencies(row.dependencies),
      },
    ],
    [],
  );

  const selectedProject = projects.find((project) => String(project.id) === selectedProjectId);

  return (
    <PageShell>
      <PageHeader
        title="Portfolio"
        subtitle="Prioritize work items with impact, effort, ROI, and dependency-aware scoring."
      />

      <Card>
        <div className="flex flex-col gap-3 p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-700">Project</label>
              <SelectInput
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                disabled={projectsLoading || projects.length === 0}
              >
                {projects.length === 0 ? <option value="">No projects available</option> : null}
                {projects.map((project) => (
                  <option key={project.id} value={String(project.id)}>
                    {project.name ?? project.code ?? project.id}
                  </option>
                ))}
              </SelectInput>
              {activeCustomerId === "all" ? (
                <div className="text-xs text-slate-500">Select a customer to see portfolio items.</div>
              ) : null}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Sort by</label>
                <SelectInput value={sortKey} onChange={(event) => setSortKey(event.target.value as SortKey)}>
                  <option value="score">Score</option>
                  <option value="impact">Impact</option>
                  <option value="effort">Effort</option>
                  <option value="roi">ROI</option>
                  <option value="title">Title</option>
                </SelectInput>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">Direction</label>
                <SelectInput
                  value={sortDirection}
                  onChange={(event) => setSortDirection(event.target.value as SortDirection)}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </SelectInput>
              </div>
            </div>
          </div>

          {error ? <div className="rounded bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}

          <div className="text-sm text-slate-600">
            Score = (impact × ROI) ÷ effort. Higher scores suggest higher priority when dependencies are clear.
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-3 p-6">
          <div className="text-sm font-semibold text-slate-700">Recommended run order</div>
          {runOrder.order.length === 0 ? (
            <div className="text-sm text-slate-500">No work items to prioritize.</div>
          ) : (
            <List as="ol" dense className="list-decimal pl-5">
              {runOrder.order.map((item) => (
                <ListItem key={item.id}>
                  <span className="font-medium text-slate-900">{item.title}</span>
                  <span className="ml-2 text-xs text-slate-500">Score {formatScore(computeScore(item))}</span>
                </ListItem>
              ))}
            </List>
          )}
          {runOrder.hasCycle ? (
            <div className="text-xs text-amber-700">
              Cyclic dependencies detected. Remaining items are ordered by score.
            </div>
          ) : null}
        </div>
      </Card>

      <Card>
        <div className="p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            {selectedProject?.name ?? selectedProject?.code ?? "Selected project"} · {sortedItems.length} items
          </div>
          <DataTable<ProjectWorkItem & { score: number }>
            data={sortedItems}
            columns={columns}
            isLoading={workItemsLoading}
          />
        </div>
      </Card>
    </PageShell>
  );
}
